import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Loader2, MapPin, Users, Clock, DollarSign, Globe, Building2, Target } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { getCatalogById } from "@/lib/queries/catalog"

export default function CatalogDetailsPage({ profile, session }: { profile: any | null, session: any | null }) {
  const { id } = useParams()

  const { data: response, isLoading } = useQuery({
    queryKey: ["catalog", id],
    queryFn: () => getCatalogById(session?.access_token, id),
    enabled: !!session?.access_token && !!id,
  })

  const catalogEntry = response?.data?.[0]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!catalogEntry) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-semibold">Opportunity not found</h2>
        <p className="text-muted-foreground mt-2">This opportunity may have been removed or is no longer available.</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8">
      <div className="grid gap-6">
        {/* Header Section with Image */}
        <div className="relative rounded-lg overflow-hidden">
          {catalogEntry.images?.[0]?.image_url && (
            <div className="aspect-[21/9] w-full">
              <img
                src={catalogEntry.images[0].image_url}
                alt={catalogEntry.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-primary/80">{catalogEntry.opportunity.type}</Badge>
              {catalogEntry.opportunity.beneficiary_types.map((type) => (
                <Badge key={type} variant="outline" className="border-white/40 text-white">{type}</Badge>
              ))}
            </div>
            <h1 className="text-3xl font-bold mb-2">{catalogEntry.title}</h1>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                <span>{catalogEntry.npo.org_name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Globe className="h-4 w-4" />
                <span>{catalogEntry.npo.city}, {catalogEntry.npo.country}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Key Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  <div>
                    <div className="font-medium">{catalogEntry.format}</div>
                    <div className="text-sm text-muted-foreground">{catalogEntry.format_details}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <div>
                    <div className="font-medium">{catalogEntry.min_volunteers}-{catalogEntry.max_volunteers}</div>
                    <div className="text-sm text-muted-foreground">Volunteers</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <div>
                    <div className="font-medium">{catalogEntry.duration}</div>
                    <div className="text-sm text-muted-foreground">Duration</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-primary" />
                  <div>
                    <div className="font-medium">${catalogEntry.event_cost}</div>
                    <div className="text-sm text-muted-foreground">Per Event</div>
                  </div>
                </div>
              </div>

              <p className="text-muted-foreground">{catalogEntry.description}</p>
            </div>

            {/* Impact Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Impact & Community Need
              </h2>
              <div className="grid gap-4">
                <div>
                  <h3 className="font-medium mb-2">The Challenge</h3>
                  <p className="text-muted-foreground whitespace-pre-line">{catalogEntry.opportunity.community_need}</p>
                </div>
                <div>
                  <h3 className="font-medium mb-2">The Impact</h3>
                  <p className="text-muted-foreground whitespace-pre-line">{catalogEntry.opportunity.impact}</p>
                </div>
                <div>
                  <h3 className="font-medium mb-2">Volunteer Output</h3>
                  <p className="text-muted-foreground">{catalogEntry.volunteer_output}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold mb-4">Key Details</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium">SDG Alignment</h4>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {catalogEntry.opportunity.sdg_alignment.map((sdg) => (
                      <Badge key={sdg} variant="outline">{sdg}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium">Relevant UN Days</h4>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {catalogEntry.opportunity.relevant_un_day.map((day) => (
                      <Badge key={day} variant="secondary">{day}</Badge>
                    ))}
                  </div>
                </div>
                {catalogEntry.prerequisites.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium">Prerequisites</h4>
                    <ul className="mt-1 space-y-1">
                      {catalogEntry.prerequisites.map((prereq, index) => (
                        <li key={index} className="text-sm text-muted-foreground">• {prereq}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold mb-4">Timeline</h3>
              <div className="space-y-3">
                {catalogEntry.timeline.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <p className="text-sm text-muted-foreground flex-1">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* What's Included Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold mb-4">What's Included</h3>
              <div className="text-sm text-muted-foreground whitespace-pre-line">
                {catalogEntry.whats_included}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}